import { Flex, Text, Button } from "@chakra-ui/react";
import { RModal } from "components";
import { IconArrow, IconRocketBg } from "icons";
import { useState, useCallback } from "react";

// Define a type for carousel slides
interface SlideData {
  description: string;
  modalHeader: string;
}

interface FuseGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  slides?: SlideData[];
}

// Sample data structure for slides
const defaultSlides: SlideData[] = [
  {
    description: 'Before racing, browse your cars to pick the best fit. A thumbs-up means the track is a Top Track for that car, with stats well-matched to the layout. You can also compare suggested track stats to your Level 2+ cars. Cars with a lock icon are ineligible and need steps to be reactivated.',
    modalHeader: 'Car Select'
  },
  {
    description: 'Forecasts affect both Tire & Stat behaviors on the track, influencing the choices players make before each race. There are six condition types and six intensity levels, each with unique effects.',
    modalHeader: 'Forecast'
  },
  {
    description: "Adjust your car's Stats in increments as precise as two decimal places, refining your setup between races to improve performance. The green suggested range on each Stat bar factors in the track layout, selected tire, base Stats, and forecasted conditions—specific to each Stat.",
    modalHeader: 'Car Setup'
  },  
];

const RaceEntryModal = ({ 
  isOpen, 
  onClose,
  slides = defaultSlides 
}: FuseGuideModalProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const handlePrevious = useCallback(() => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, [slides.length]);

  const handleNext = useCallback(() => {
    if (currentSlide === slides.length - 1) {
      onClose();
    } else {
      setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }
  }, [slides.length, currentSlide, onClose]);

  return (
    <RModal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      title={slides[currentSlide].modalHeader}
      icon={<IconRocketBg />}
      modalVariant="blue"
      footer={
        <Flex 
          justify="space-between" 
          w="full"
          gap="4"
        >
          {currentSlide > 0 && (
            <Button 
              variant="secondary-gradient"
              onClick={handlePrevious}
            >
              <IconArrow />
            </Button>
          )}
            
          
          <Button
            variant="unstyled"
            onClick={handleNext}
            w="full"
            borderRadius="0.25rem"
            background="linear-gradient(225deg, rgba(109, 177, 241, 0.2) 0%, rgba(79, 138, 197, 0.2) 18%, rgba(38, 102, 166, 0.2) 73%, rgba(26, 59, 116, 0.2) 100%)"
            position="relative"
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: '0.25rem',
              background: 'linear-gradient(225deg, rgba(109, 177, 241, 0.2) 0%, rgba(79, 138, 197, 0.2) 18%, rgba(38, 102, 166, 0.2) 73%, rgba(26, 59, 116, 0.2) 100%)',
              padding: '2px',
              mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              maskComposite: "exclude",
              zIndex: -1,
            }}
            color="rgba(109, 177, 241, 0.8)"
            _hover={{
              background: "linear-gradient(225deg, rgba(109, 177, 241, 0.4) 0%, rgba(79, 138, 197, 0.4) 18%, rgba(38, 102, 166, 0.4) 73%, rgba(26, 59, 116, 0.4) 100%)",
              color: "rgba(109, 177, 241, 1)"
            }}
            transition="all 0.3s ease"
          >
            {currentSlide === slides.length - 1 ? 'LET\'S GO!' : 'NEXT'}
          </Button>
        </Flex>
      }
    >
      <Flex
        flexDir="column"
        gap="4"
      >
        {/* Carousel slide */}
        <Flex
          position="relative"
          w="full"
          borderRadius="0.5rem"
        >
          <Text
            fontSize={{ base: '0.875rem', md: '1.125rem'}}
            lineHeight={{ base: '1.125rem', md: '1.5rem'}}
            fontWeight={500}
            color="white.60"
          >
            {slides[currentSlide].description}
          </Text>
        </Flex>

        {/* Navigation buttons */}
        
      </Flex>
    </RModal>
  );
};

export default RaceEntryModal;